import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { AlertTriangle, Plus, X, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const MedicineInteractionChecker = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const [medicines, setMedicines] = useState([]);
  const [currentMedicine, setCurrentMedicine] = useState('');
  const [checking, setChecking] = useState(false);
  const [interactions, setInteractions] = useState(null);

  const addMedicine = () => {
    if (currentMedicine.trim()) {
      setMedicines([...medicines, currentMedicine.trim()]);
      setCurrentMedicine('');
      setInteractions(null);
    }
  };

  const removeMedicine = (index) => {
    setMedicines(medicines.filter((_, i) => i !== index));
    setInteractions(null);
  };

  const checkInteractions = async () => {
    if (medicines.length < 2) {
      toast({
        title: 'Add More Medicines',
        description: 'Add at least 2 medicines to check for interactions',
        variant: 'default'
      });
      return;
    }

    setChecking(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/health/medicine/check-interactions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ medicines })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setInteractions(data);
      }
    } catch (error) {
      console.error('Error checking interactions:', error);
      toast({
        title: 'Check Failed',
        description: 'Unable to check interactions',
        variant: 'destructive'
      });
    } finally {
      setChecking(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            Medicine Interaction Checker
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-xs text-yellow-800">
              ⚠️ This is AI-generated information. Always consult a healthcare professional or pharmacist.
            </p>
          </div>

          {/* Add Medicine Input */}
          <div className="flex gap-2">
            <Input
              placeholder="Enter medicine name..."
              value={currentMedicine}
              onChange={(e) => setCurrentMedicine(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addMedicine()}
            />
            <Button onClick={addMedicine} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {/* Medicine List */}
          {medicines.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Current Medicines:</p>
              <div className="flex flex-wrap gap-2">
                {medicines.map((med, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="flex items-center gap-2 py-1 px-3"
                  >
                    {med}
                    <X
                      className="h-3 w-3 cursor-pointer"
                      onClick={() => removeMedicine(index)}
                    />
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Check Button */}
          {medicines.length >= 2 && (
            <Button
              onClick={checkInteractions}
              disabled={checking}
              className="w-full bg-red-600 hover:bg-red-700"
            >
              {checking ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Checking Interactions...
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Check for Interactions
                </>
              )}
            </Button>
          )}

          {/* Results */}
          {interactions && (
            <div className="bg-white rounded-lg border p-4">
              <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                {interactions.interaction_check}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MedicineInteractionChecker;